/**
 * Simple in-memory image cache with TTL and LRU eviction
 * Caches images as blob URLs to avoid repeated network requests
 */

interface CacheEntry {
    blobUrl: string;
    timestamp: number;
    originalUrl: string;
}

class ImageCache {
    private readonly cache: Map<string, CacheEntry>;
    private readonly inFlightRequests: Map<string, Promise<string>>;
    private readonly maxSize: number;
    private readonly ttl: number; // Time to live in milliseconds
    private readonly enabled: boolean;
    private cleanupIntervalId: NodeJS.Timeout | null = null;

    constructor(maxSize = 200, ttlMinutes = 30) {
        this.cache = new Map();
        this.inFlightRequests = new Map();
        this.maxSize = maxSize;
        this.ttl = ttlMinutes * 60 * 1000;
        // Allow disabling cache via window flag for debugging
        this.enabled =
            typeof window !== 'undefined'
                ? (window as any).__IMAGE_CACHE_ENABLED__ !== false
                : true;
    }

    /**
     * Get an image from cache or fetch it if not cached
     */
    async get(url: string): Promise<string> {
        // Return original URL if caching is disabled
        if (!this.enabled) {
            return url;
        }

        // Return original URL if it's not an API endpoint (e.g., SVG data URLs or local files)
        if (!url?.includes('/api/internal/')) {
            return url;
        }

        // Start cleanup timer on first use (lazy initialization)
        this.startCleanupTimer();

        const cached = this.cache.get(url);

        // Check if cache entry exists and is not expired
        if (cached && Date.now() - cached.timestamp < this.ttl) {
            // Move to end (LRU)
            this.cache.delete(url);
            this.cache.set(url, cached);
            return cached.blobUrl;
        }

        // If expired, revoke the old blob URL
        if (cached) {
            URL.revokeObjectURL(cached.blobUrl);
            this.cache.delete(url);
        }

        // Check if there's already an in-flight request for this URL (deduplication)
        const existingRequest = this.inFlightRequests.get(url);
        if (existingRequest !== undefined) {
            return existingRequest;
        }

        // Create and store the fetch promise to deduplicate concurrent requests
        const fetchPromise = this.fetchAndCache(url);
        this.inFlightRequests.set(url, fetchPromise);

        // Clean up the in-flight request when done (success or failure)
        fetchPromise.finally(() => {
            this.inFlightRequests.delete(url);
        });

        return fetchPromise;
    }

    /**
     * Fetch an image and cache it
     * Separate method to enable request deduplication
     */
    private async fetchAndCache(url: string): Promise<string> {
        try {
            const response = await fetch(url, {
                credentials: 'include', // Include cookies for all requests (matches HomeClient.withCredentials)
                mode: 'cors', // Required for CORS-enabled endpoints
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            // Evict oldest entry if cache is full (LRU)
            if (this.cache.size >= this.maxSize) {
                const firstKey = this.cache.keys().next().value;
                const oldEntry = this.cache.get(firstKey);
                if (oldEntry) {
                    URL.revokeObjectURL(oldEntry.blobUrl);
                }
                this.cache.delete(firstKey);
            }

            // Add to cache
            this.cache.set(url, {
                blobUrl,
                timestamp: Date.now(),
                originalUrl: url,
            });

            return blobUrl;
        } catch (error) {
            console.error('Error caching image:', url, error);
            // Return original URL as fallback
            return url;
        }
    }

    /**
     * Check if an image is in cache and not expired
     */
    has(url: string): boolean {
        const cached = this.cache.get(url);
        if (!cached) return false;

        const isExpired = Date.now() - cached.timestamp >= this.ttl;
        if (isExpired) {
            URL.revokeObjectURL(cached.blobUrl);
            this.cache.delete(url);
            return false;
        }

        return true;
    }

    /**
     * Get cached blob URL synchronously (does not fetch if not cached)
     * Returns the original URL if caching is disabled or URL is not an API endpoint
     * Returns undefined if URL is an API endpoint but not in cache
     */
    getCached(url: string | undefined): string | undefined {
        if (!this.enabled || !url?.includes('/api/internal/')) {
            return url;
        }

        const cached = this.cache.get(url);
        if (!cached) return undefined;

        const isExpired = Date.now() - cached.timestamp >= this.ttl;
        if (isExpired) {
            URL.revokeObjectURL(cached.blobUrl);
            this.cache.delete(url);
            return undefined;
        }

        // Move to end (LRU)
        this.cache.delete(url);
        this.cache.set(url, cached);

        return cached.blobUrl;
    }

    /**
     * Clear the entire cache and stop cleanup timer
     */
    clear(): void {
        this.cache.forEach((entry) => {
            URL.revokeObjectURL(entry.blobUrl);
        });
        this.cache.clear();
        this.inFlightRequests.clear();
        this.stopCleanupTimer();
    }

    /**
     * Remove expired entries from cache
     */
    cleanup(): void {
        const now = Date.now();
        const expiredKeys: string[] = [];

        this.cache.forEach((entry, key) => {
            if (now - entry.timestamp >= this.ttl) {
                expiredKeys.push(key);
                URL.revokeObjectURL(entry.blobUrl);
            }
        });

        expiredKeys.forEach((key) => this.cache.delete(key));
    }

    /**
     * Get cache statistics
     */
    getStats() {
        return {
            size: this.cache.size,
            maxSize: this.maxSize,
            ttl: this.ttl,
        };
    }

    /**
     * Start the periodic cleanup timer
     * Called automatically when cache is first used
     */
    private startCleanupTimer(): void {
        // Don't start if already running, disabled, or in non-browser environment
        if (
            this.cleanupIntervalId ||
            !this.enabled ||
            typeof window === 'undefined'
        ) {
            return;
        }

        this.cleanupIntervalId = setInterval(
            () => {
                this.cleanup();
            },
            5 * 60 * 1000,
        ); // Run cleanup every 5 minutes
    }

    /**
     * Stop the periodic cleanup timer
     * Useful for tests and cleanup
     */
    stopCleanupTimer(): void {
        if (this.cleanupIntervalId) {
            clearInterval(this.cleanupIntervalId);
            this.cleanupIntervalId = null;
        }
    }
}

// Singleton instance
const imageCache = new ImageCache(200, 30); // Cache 200 images for 30 minutes

export default imageCache;
